import { ArrowLeft, ArrowRight, Check, ChevronLeft, CircleCheck, Clock } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import type { CourseDetail, LessonView } from "../../schema/index.ts";
import { pickText, type Locale } from "../../schema/core.ts";
import { useSession } from "../auth.ts";
import { ErrorState } from "../components/ErrorState.tsx";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { BlockRenderer } from "../player/BlockRenderer.tsx";
import { useCourseKit } from "../provider.tsx";
import {
  isNotFound,
  useCompleteLesson,
  useCompletedLessons,
  useCourse,
  useLessonView,
} from "../queries.ts";
import { coursePath, lessonPath, loginPath, useDocumentTitle } from "../routing.ts";
import { Button } from "../ui/Button.tsx";
import { cn } from "../ui/cn.ts";
import { PageLoading } from "../ui/Spinner.tsx";
import { toast } from "../ui/toast.tsx";
import { NotFoundPage } from "./NotFoundPage.tsx";

/** `/cursus/:courseSlug/:lessonSlug` */
export const LessonPage = (): ReactNode => {
  const { courseSlug = "", lessonSlug = "" } = useParams();
  const { locale } = useCourseKit();
  const copy = useSiteCopy();
  const view = useLessonView(courseSlug, lessonSlug);
  // Lesson count and sidebar; usually cached from the overview already.
  const course = useCourse(courseSlug);
  useDocumentTitle(view.data ? pickText(view.data.lesson.title, locale) : null);

  if (view.isPending) return <PageLoading label={copy.common.loading} />;
  if (view.isError) {
    return isNotFound(view.error) ? (
      <NotFoundPage />
    ) : (
      <ErrorState onRetry={() => void view.refetch()} />
    );
  }
  return <LessonBody view={view.data} course={course.data} locale={locale} />;
};

interface LessonBodyProps {
  view: LessonView;
  course: CourseDetail | undefined;
  locale: Locale;
}

const LessonBody = ({ view, course, locale }: LessonBodyProps): ReactNode => {
  const copy = useSiteCopy();
  const { lesson, previous, next } = view;
  const courseSlug = view.course.slug;
  const { completed } = useCompletedLessons(courseSlug);
  const summary = pickText(lesson.summary, locale).trim();

  return (
    <div
      className={cn(
        "mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12",
        "lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-12",
      )}>
      <article className="min-w-0">
        <nav aria-label={copy.lesson.breadcrumb}>
          <Link
            to={coursePath(courseSlug)}
            className={cn(
              "inline-flex min-h-11 items-center gap-1 rounded-lg text-base font-semibold",
              "text-primary hover:underline",
            )}>
            <ChevronLeft aria-hidden="true" className="size-5" />
            {pickText(view.course.title, locale)}
          </Link>
        </nav>

        <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-base text-muted-foreground">
          <span className="font-semibold text-primary">
            {course
              ? copy.lesson.lessonOf(lesson.position, course.lessons.length)
              : copy.course.lessonNumber(lesson.position)}
          </span>
          {lesson.durationMinutes !== null && (
            <span className="inline-flex items-center gap-1">
              <Clock aria-hidden="true" className="size-4" />
              {copy.common.minutes(lesson.durationMinutes)}
            </span>
          )}
        </p>
        <h1 className="mt-2 text-4xl font-semibold sm:text-5xl">
          {pickText(lesson.title, locale)}
        </h1>
        {summary && (
          <p className="mt-4 max-w-[68ch] text-xl leading-relaxed text-muted-foreground">
            {summary}
          </p>
        )}

        <div className="mt-10">
          <BlockRenderer blocks={lesson.blocks} locale={locale} />
        </div>

        <FinishLesson view={view} done={completed.has(lesson.id)} />

        <nav
          aria-label={copy.lesson.pagination}
          className="mt-10 grid gap-3 border-t border-border pt-8 sm:grid-cols-2">
          {previous ? (
            <Link
              to={lessonPath(courseSlug, previous.slug)}
              className={cn(
                "group flex flex-col rounded-2xl border border-border bg-card p-4",
                "hover:border-primary",
              )}>
              <span className="inline-flex items-center gap-1 text-base text-muted-foreground">
                <ArrowLeft aria-hidden="true" className="size-4" />
                {copy.lesson.previous}
              </span>
              <span className="mt-1 font-semibold group-hover:text-primary group-hover:underline">
                {pickText(previous.title, locale)}
              </span>
            </Link>
          ) : (
            <span className="hidden sm:block" />
          )}
          {next ? (
            <Link
              to={lessonPath(courseSlug, next.slug)}
              className={cn(
                "group flex flex-col rounded-2xl border border-border bg-card p-4 text-right",
                "hover:border-primary",
              )}>
              <span
                className={cn(
                  "inline-flex items-center justify-end gap-1 text-base",
                  "text-muted-foreground",
                )}>
                {copy.lesson.next}
                <ArrowRight aria-hidden="true" className="size-4" />
              </span>
              <span className="mt-1 font-semibold group-hover:text-primary group-hover:underline">
                {pickText(next.title, locale)}
              </span>
            </Link>
          ) : (
            <Link
              to={coursePath(courseSlug)}
              className={cn(
                "group flex flex-col justify-center rounded-2xl border border-border bg-card p-4",
                "text-right font-semibold hover:border-primary hover:text-primary hover:underline",
              )}>
              {copy.lesson.backToCourse}
            </Link>
          )}
        </nav>
      </article>

      {course && (
        <aside className="hidden lg:block">
          <nav aria-label={copy.lesson.lessonsInCourse} className="sticky top-24">
            <p className="mb-3 text-base font-semibold tracking-wide text-muted-foreground uppercase">
              {copy.lesson.lessonsInCourse}
            </p>
            <ol className="flex flex-col gap-1">
              {course.lessons.map((item) => {
                const current = item.id === lesson.id;
                const done = completed.has(item.id);
                return (
                  <li key={item.id}>
                    <Link
                      to={lessonPath(courseSlug, item.slug)}
                      aria-current={current ? "page" : undefined}
                      className={cn(
                        "flex min-h-11 items-start gap-2.5 rounded-xl px-3 py-2 text-base",
                        "hover:bg-muted",
                        current && "bg-card font-semibold shadow-sm ring-1 ring-border",
                      )}>
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-0.5 grid size-6 shrink-0 place-items-center rounded-full",
                          "text-sm font-semibold",
                          done ? "bg-success text-primary-foreground" : "bg-muted text-foreground",
                        )}>
                        {done ? <Check className="size-3.5" /> : item.position}
                      </span>
                      <span className="min-w-0 flex-1 leading-snug">
                        {pickText(item.title, locale)}
                        {done && <span className="sr-only"> ({copy.course.lessonDone})</span>}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </nav>
        </aside>
      )}
    </div>
  );
};

/** Finish button for learners; a sign-in prompt for visitors. */
const FinishLesson = ({ view, done }: { view: LessonView; done: boolean }): ReactNode => {
  const copy = useSiteCopy().lesson;
  const { user, loading } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const courseSlug = view.course.slug;
  const complete = useCompleteLesson(courseSlug);

  if (loading) return null;

  if (!user) {
    return (
      <div className="mt-12 rounded-2xl border border-border bg-muted/60 p-5">
        <p>{copy.signedOutNote}</p>
        <Link
          to={loginPath(`${location.pathname}${location.search}`)}
          className={cn(
            "mt-2 inline-flex min-h-11 items-center font-semibold text-primary underline",
            "underline-offset-4",
          )}>
          {copy.signedOutCta}
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <p
        className={cn(
          "mt-12 flex items-center gap-2 rounded-2xl border border-success/35 bg-success/8 p-5",
          "font-semibold text-success",
        )}>
        <CircleCheck aria-hidden="true" className="size-6 shrink-0" />
        {copy.alreadyDone}
      </p>
    );
  }

  return (
    <div className="mt-12">
      <Button
        size="lg"
        className="w-full sm:w-auto"
        loading={complete.isPending}
        onClick={() =>
          complete.mutate(view.lesson.id, {
            onSuccess: () => {
              if (view.next) {
                toast.success(copy.finished);
                void navigate(lessonPath(courseSlug, view.next.slug));
              } else {
                toast.success(copy.courseFinished);
                void navigate(coursePath(courseSlug));
              }
            },
            onError: () => toast.error(copy.finishError),
          })
        }>
        <Check aria-hidden="true" className="size-5" />
        {copy.finish}
      </Button>
    </div>
  );
};
